'use client';

import { PERMISSIONS } from '@terra/shared-authorization';
import { ADAPTIVE_SCANNING_ENABLED_FLAG } from '@terra/shared-contracts-feature-flags';
import {
  AdaptiveScanningPausedAlert,
  AdaptiveScanningResumeDialog,
} from '@terra/ui-design-system';
import { useAdaptiveScanningState } from '../../hooks/api/useAdaptiveScanningState';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { usePermissions } from '../../hooks/usePermissions';
import { useAdaptiveScanningResume } from './automation/useAdaptiveScanningResume';
import { useNavigateToBlockedSignals } from './automation/useNavigateToBlockedSignals';

type AdaptivePausedBadgeProps = {
  applicationId: string;
};

export function AdaptivePausedBadge({
  applicationId,
}: AdaptivePausedBadgeProps) {
  const { isFeatureFlagEnabled } = useFeatureFlags(
    ADAPTIVE_SCANNING_ENABLED_FLAG,
  );
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(PERMISSIONS.MANAGE_APPLICATIONS.name);
  const { data } = useAdaptiveScanningState({
    applicationId,
    enabled: isFeatureFlagEnabled,
  });
  const { isResumeOpen, setIsResumeOpen, isResuming, confirmResume } =
    useAdaptiveScanningResume({ applicationId });
  const navigateToBlockedSignals = useNavigateToBlockedSignals({
    applicationId,
  });

  if (
    !isFeatureFlagEnabled ||
    data?.enabled === false ||
    data?.phase !== 'paused_blocked'
  ) {
    return null;
  }

  return (
    <>
      <AdaptiveScanningPausedAlert
        pausedReason={data.pausedReason}
        canResume={canManage}
        onView={navigateToBlockedSignals}
        onResume={() => setIsResumeOpen(true)}
      />
      <AdaptiveScanningResumeDialog
        open={isResumeOpen}
        state={data}
        isSubmitting={isResuming}
        onOpenChange={setIsResumeOpen}
        onConfirm={confirmResume}
      />
    </>
  );
}
